import React, { useEffect, useState } from 'react';
import { Div, Gutter, Loader, Title } from '@gaiads/telia-react-component-library';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CountrySwitcher, CountrySwitcherOption, PageHeader } from '@cdaas/reusable-components-daas';
import { AdminProductEnvelope, AdminProductVariant, Country, Material } from '@cdaas/api-clients/dist/product-service-openapi';

import { ProductFormParamTypes } from '../ProductForm/ProductForm';
import { resetNotification, setNotification } from '../../store/ui';
import MaterialCodesCard from './MaterialCodesCard/MaterialCodesCard';
import ProductInformationCard from './ProductInformationCard/ProductInformationCard';
import ProductPricingCard from './ProductPricingCard/ProductPricingCard';
import { selectCurrentLocaleId } from '../../store/locales';
import { getCountryOptions } from '../../utils/locales';
import MaterialCodeEditModal from './MaterialCodeEditModal/MaterialCodeEditModal';
import PricingEditModal from './PricingEditModal/PricingEditModal';
import { updateEntity } from '../../utils/entities';
import {
  productServiceAdminMaterialsClient,
  productServiceAdminProductsClient,
  productServiceCountriesClient,
} from '../../api-client/product-service-client';
import { getVariantsPropertyValues } from '../../utils/products';
import { selectFeatureFlags } from '../../store/featureFlags';
import { Norway } from '../../utils/countries';

const ProductMainScreen: React.FC = () => {
  const productId = useParams<ProductFormParamTypes>().productId;
  const countryCode = useParams<{ countryCode?: string }>().countryCode;

  const dispatch = useDispatch();
  const history = useHistory();

  const { t } = useTranslation('productscreen');

  const [product, setProduct] = useState<AdminProductEnvelope>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [variantPropertyValues, setVariantPropertyValues] = useState<Record<number, string[]>>();
  const [selectedCountry, setSelectedCountry] = useState<CountrySwitcherOption>();
  const [countries, setCountries] = useState<Country[]>([]);
  const [countryOptions, setCountryOptions] = useState<CountrySwitcherOption[]>([]);
  const [editingVariantId, setEditingVariantId] = useState<AdminProductVariant['id']>();
  const [editPricingModalOpen, setEditPricingModalOpen] = useState<boolean>(false);

  const editingVariant = product?.product.variants?.find(variant => variant.id === editingVariantId);

  const localeId = useSelector(selectCurrentLocaleId);
  const priceMasterEnabled = useSelector(selectFeatureFlags).FEATURE_FLAG_PRICE_MASTER_ENABLED;

  const translation = localeId ? product?.product?.translations?.find(translation => translation.locale_id === localeId) : undefined;

  const fetchCountries = async () => {
    try {
      const response = await productServiceCountriesClient.getCountries();
      const { countries } = response.data;
      setCountries(countries);
      const initCountryOptions = getCountryOptions(countries);
      setCountryOptions(initCountryOptions);
      let selectedCountry = initCountryOptions[0]; // Default
      if (countryCode) {
        const countryId = countries.find(country => country.code.toUpperCase() === countryCode?.toUpperCase())?.id;
        selectedCountry = initCountryOptions.find(countryOption => countryOption.value === String(countryId)) || initCountryOptions[0];
      }
      setSelectedCountry(selectedCountry);
    } catch {
      dispatch(
        setNotification({
          text: t('errors.fetch'),
          show: true,
          type: 'warning',
        }),
      );
    }
  };

  const fetchProductAndProductRelatedData = async (productId?: string) => {
    setLoading(true);
    try {
      if (productId) {
        const response = await productServiceAdminProductsClient.getAdminProduct(+productId);
        setProduct(response.data);
        const { product, property_values } = response.data;
        setVariantPropertyValues(getVariantsPropertyValues(product?.variants, property_values));
      }
    } catch {
      dispatch(
        setNotification({
          text: t('errors.productInitialFetchError'),
          show: true,
          type: 'warning',
        }),
      );
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductAndProductRelatedData(productId);
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  if (loading) {
    return (
      <Div alignTextToCenter padding={{ top: 'xlg' }}>
        <Loader size="lg" data-testid="loader" />
      </Div>
    );
  } else if (error) {
    return (
      <Div alignTextToCenter padding={{ top: 'xlg' }}>
        <Title>{t('errors.productInitialFetchError')}</Title>
      </Div>
    );
  }

  const handleCountryChange = (selected: CountrySwitcherOption) => {
    setSelectedCountry(selected);
    const code = countries.find(country => country.id === Number(selected.value))?.code;
    if (code) {
      // Default country is not shown in URL
      if (code === countries[0].code) {
        history.replace(`/product/${productId}/main`);
      } else {
        history.replace(`/product/${productId}/main/${code.toLowerCase()}`);
      }
    }
  };

  const handleMaterialCodeSave = async (code?: string) => {
    try {
      if (selectedCountry && editingVariant && code && product) {
        setLoading(true);
        const country_id = Number(selectedCountry.value);
        const material = editingVariant?.materials?.find(currentMaterial => currentMaterial.country_id === country_id);
        const updatedMaterial: Material = {
          id: 0,
          ...(material || {}),
          country_id,
          variant_id: editingVariant.id,
          code,
        };
        const responseMaterial = material
          ? (await productServiceAdminMaterialsClient.putMaterial(material.id, updatedMaterial)).data
          : (await productServiceAdminMaterialsClient.postMaterial(updatedMaterial)).data;

        if (priceMasterEnabled && selectedCountry.countryFlag === Norway.countryFlag) {
          // refresh product data after delay, so price data has time to update
          await new Promise(resolve => setTimeout(resolve, 2000)).then(() => fetchProductAndProductRelatedData(productId));
        } else {
          const materials = material
            ? updateEntity(editingVariant.materials, responseMaterial)
            : [...(editingVariant.materials || []), responseMaterial];

          setProduct({
            ...product,
            product: {
              ...product.product,
              variants: updateEntity(product.product.variants, {
                ...editingVariant,
                materials,
              }),
            },
          });
        }
        setEditingVariantId(undefined);
        dispatch(resetNotification());
      } else throw new Error();
    } catch {
      dispatch(
        setNotification({
          text: t('errors.materialCodeSave'),
          show: true,
          type: 'warning',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAcquisitionPricesSave = async (variantsWithChangedAcquisitionPrices: AdminProductVariant[]) => {
    if (!product?.product?.id) return;
    const updatedProduct = { ...product?.product, variants: variantsWithChangedAcquisitionPrices };
    if (!updatedProduct.id) return;
    setLoading(true);
    try {
      await productServiceAdminProductsClient.putAdminProduct(updatedProduct.id, updatedProduct);
      // TODO putAdminProduct doesn't return all product data, that's why we have to fetch product data from getAdminProduct again
      const response = await productServiceAdminProductsClient.getAdminProduct(+product.product.id);
      setProduct(response.data);
    } catch {
      dispatch(
        setNotification({
          text: t('errors.acquisitionPriceSave'),
          show: true,
          type: 'warning',
        }),
      );
    } finally {
      setLoading(false);
      setEditPricingModalOpen(false);
    }
  };

  return (
    <>
      <Div margin={{ vertical: 'lg', horizontal: 'xxlg' }}>
        <PageHeader
          subheaderText={t('mainScreen.subheaderText')}
          /* waits for new design
          badgeBackgroundColor="orange400"
          badgeColor="orange800"
          badgeText={t('mainScreen.partiallyAvailableBadge')} */
          uppercase
        >
          {translation?.name || product?.product.name}
        </PageHeader>
        <Gutter size="xlg" />
        {selectedCountry && (
          <Div style={{ maxWidth: '400px' }}>
            <CountrySwitcher
              onChange={handleCountryChange}
              options={countryOptions}
              selectedOption={selectedCountry}
              translations={{ countryFlagAltText: t('mainScreen.altFlagIcon') }}
            />
          </Div>
        )}
        <Div style={{ maxWidth: '800px' }}>
          <Gutter size="md" />
          <ProductInformationCard product={product?.product} translation={translation} />
          <Gutter size="md" />
          <MaterialCodesCard
            variants={product?.product.variants || []}
            variantPropertyValues={variantPropertyValues}
            productName={translation?.name || product?.product.name}
            countryId={Number(selectedCountry?.value)}
            countries={countries}
            onEditClick={setEditingVariantId}
          />
          <Gutter size="md" />
          <ProductPricingCard
            variants={product?.product.variants || []}
            countryId={Number(selectedCountry?.value)}
            countries={countries}
            onEditClick={() => setEditPricingModalOpen(true)}
            onRefreshProductDataClick={() => fetchProductAndProductRelatedData(productId)}
          />
        </Div>
        <MaterialCodeEditModal
          variant={editingVariant}
          variantPropertyValues={variantPropertyValues}
          productName={translation?.name || product?.product.name}
          countryId={Number(selectedCountry?.value)}
          countries={countries}
          onAction={handleMaterialCodeSave}
          onClose={() => setEditingVariantId(undefined)}
        />
        <PricingEditModal
          open={editPricingModalOpen}
          productName={product?.product.name}
          variants={product?.product.variants || []}
          countryId={Number(selectedCountry?.value)}
          countries={countries}
          onAction={variantsWithChangedAcquisitionPrices => handleAcquisitionPricesSave(variantsWithChangedAcquisitionPrices)}
          onClose={() => setEditPricingModalOpen(false)}
        />
      </Div>
    </>
  );
};

export default ProductMainScreen;
